// OnboardingPage.jsx


import React, { useState, useRef } from 'react';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, Grid, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUserOnboarding } from '../../../hooks/useUserOnboarding';
import { uploadUserDocument } from '../../../helpers/documentUpload';
import { useAuth } from '../../../hooks/useAuth';
import OnboardingStyles from './OnboardingPage.styles';
import LegalTerms from './LegalTerms';

const OnboardingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });

  const [disclosureAccepted, setDisclosureAccepted] = useState(false);
  const [photoId, setPhotoId] = useState(null);
  const [headshot, setHeadshot] = useState(null);
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { loading, error: onboardingError, submitUserOnboarding } = useUserOnboarding();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e, setter) => {
    setter(e.target.files[0]);
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/png');
    setHeadshot(dataUrl);
  };

  const enableCamera = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          videoRef.current.srcObject = stream;
        })
        .catch(() => {
          setError('Camera access was denied or not supported by your device.');
        });
    } else {
      setError('Your browser does not support camera access.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.address ||
      !formData.city ||
      !formData.state ||
      !formData.zip ||
      !photoId ||
      !headshot ||
      !disclosureAccepted
    ) {
      setError('Please fill out all fields, upload the required files, and accept the disclosures.');
      return;
    }

    setError('');

    try {
      // 1. Upload the ID document
      const idUpload = await uploadUserDocument(photoId, currentUser.uid);

      // 2. Prepare the headshot (handle camera data URL)
      let headshotFileOrBlob = headshot;
      if (typeof headshot === 'string' && headshot.startsWith('data:')) {
        const arr = headshot.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        headshotFileOrBlob = new Blob([u8arr], { type: mime });
      }
      const headshotUpload = await uploadUserDocument(headshotFileOrBlob, currentUser.uid);

      // 3. Build the onboarding user doc
      const userDoc = {
        ...formData,
        disclosureAccepted,
        idDocument: idUpload ? {
          docId: idUpload.docId || '',
          path: idUpload.path || '',
          filename: idUpload.filename || '',
          label: 'Photo ID',
          type: 'id',
        } : null,
        headshot: headshotUpload ? {
          docId: headshotUpload.docId || '',
          path: headshotUpload.path || '',
          filename: headshotUpload.filename || '',
          label: 'Headshot',
          type: 'headshot',
        } : null,
      };

      // Remove any keys that are null (tidy)
      Object.keys(userDoc).forEach(key => {
        if (userDoc[key] === null) delete userDoc[key];
      });

      // 4. Save to Firestore (your user record)
      await submitUserOnboarding(currentUser.uid, userDoc);

      // 5. Go to next onboarding step
      navigate('/user-questionnaire-onboarding');
    } catch (err) {
      console.error('Error during onboarding:', err);
      let errorMessage = 'Failed to complete onboarding.';
      if (err.response) {
        errorMessage += ` Server responded with: ${err.response.data.error || err.response.statusText}`;
      } else if (err.request) {
        errorMessage += ' No response from server. Check if it’s running.';
      }
      setError(errorMessage);
    }
  };



  return (
    <Box sx={OnboardingStyles.rootBox}>
      <Paper sx={OnboardingStyles.paper} elevation={6}>
        <Typography variant="h4" component="h1" sx={OnboardingStyles.header}>
          User Onboarding
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            label="Full Legal Name"
            name="name"
            variant="outlined"
            fullWidth
            required
            value={formData.name}
            onChange={handleInputChange}
            sx={OnboardingStyles.textField}
          />

          <TextField
            label="Street Address"
            name="address"
            variant="outlined"
            fullWidth
            required
            value={formData.address}
            onChange={handleInputChange}
            sx={OnboardingStyles.textField}
          />

          <TextField
            label="City"
            name="city"
            variant="outlined"
            fullWidth
            required
            value={formData.city}
            onChange={handleInputChange}
            sx={OnboardingStyles.textField}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="State"
                name="state"
                variant="outlined"
                fullWidth
                required
                value={formData.state}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="ZIP Code"
                name="zip"
                variant="outlined"
                fullWidth
                required
                value={formData.zip}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          <Typography variant="body1" sx={OnboardingStyles.disclosureText}>
            By proceeding, you acknowledge that you have read and accept the
            terms and conditions outlined below.
          </Typography>

          {/* Terms & Conditions Scrollable Block */}
          <Paper
            elevation={3}
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              p: 2,
              my: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <LegalTerms />

            {/* You can continue adding each section in its own <Typography> block.
      For brevity, here’s a collapsed snippet – just expand with the rest of your full T&C text. */}
            <Typography variant="body2">
              {/* ...Full text of sections 1 through 30 goes here... */}
            </Typography>
          </Paper>

          <FormControlLabel
            control={
              <Checkbox
                checked={disclosureAccepted}
                onChange={(e) => setDisclosureAccepted(e.target.checked)}
                color="primary"
                required
              />
            }
            label="I accept the terms and conditions"
          />

          <Box sx={OnboardingStyles.fileUploadButton}>
            <Button variant="contained" component="label">
              Upload Photo ID
              <input
                type="file"
                hidden
                accept=".jpg,.png,.pdf"
                onChange={(e) => handleFileChange(e, setPhotoId)}
              />
            </Button>
            {photoId && (
              <Typography variant="body2">
                Uploaded file: {photoId.name}
              </Typography>
            )}
          </Box>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Upload Headshot or Take a Photo
          </Typography>
          <Box sx={OnboardingStyles.headshotSection}>
            <Button variant="contained" component="label">
              Upload Headshot
              <input
                type="file"
                hidden
                accept=".jpg,.png"
                onChange={(e) => handleFileChange(e, setHeadshot)}
              />
            </Button>
            <Button variant="outlined" onClick={enableCamera}>
              Use Camera
            </Button>
          </Box>

          <Box>
            <video ref={videoRef} style={OnboardingStyles.video} autoPlay />
            <canvas ref={canvasRef} style={OnboardingStyles.canvas}></canvas>
            {headshot && (
              <img
                src={headshot}
                alt="Headshot"
                style={OnboardingStyles.headshotImg}
              />
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleCapturePhoto}
            sx={{ mb: 3 }}
          >
            Capture Photo
          </Button>

          {error && (
            <Typography color="error" sx={OnboardingStyles.errorText}>
              {error}
            </Typography>
          )}
          {onboardingError && (
            <Typography color="error" sx={OnboardingStyles.errorText}>
              {onboardingError}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={OnboardingStyles.submitButton}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Complete Onboarding"}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default OnboardingPage;

